// InvoiceDealSummary — полоска Итого/Оплачено/Остаток на обзоре сделки (V17 W2).
// Тот же computeSummary и queryKey ['invoices','deal',dealId] size=100, что InvoiceBlock.

import { useQuery } from '@tanstack/react-query';
import { Box, Stack, Typography } from '@mui/material';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { formatMoney } from '@/lib/format';
import { t } from '@/i18n';

/** Размер страницы сводки (KISS-канон §3.6: FE-агрегация, BE-кап 100). */
export const INVOICE_SUMMARY_PAGE_SIZE = 100;

/** Сводка по счетам сделки (Σ по валюте сделки). */
export interface InvoiceSummary {
  total: number;
  paid: number;
  remaining: number;
}

/**
 * Считает Итого/Оплачено/Остаток по счетам (только валюта сделки; пусто → нулевая сводка).
 *
 * @param invoices счета страницы (size=100)
 * @param dealCurrency валюта сделки; без неё суммируются все счета
 * @returns суммы amount / paidAmount / remaining
 */
export function computeSummary(invoices: Invoice[], dealCurrency: string | undefined): InvoiceSummary {
  const sameCurrency = dealCurrency ? invoices.filter((i) => i.currency === dealCurrency) : invoices;
  return sameCurrency.reduce(
    (acc, i) => ({
      total: acc.total + i.amount,
      paid: acc.paid + i.paidAmount,
      remaining: acc.remaining + i.remaining,
    }),
    { total: 0, paid: 0, remaining: 0 },
  );
}

interface InvoiceSummaryStripProps {
  summary: InvoiceSummary;
  currency?: string | undefined;
}

/** Поля полоски: ключ сводки → i18n-подпись. */
const SUMMARY_FIELDS: ReadonlyArray<{ key: keyof InvoiceSummary; labelKey: 'invoices.block.total' | 'invoices.block.paid' | 'invoices.block.remaining' }> = [
  { key: 'total', labelKey: 'invoices.block.total' },
  { key: 'paid', labelKey: 'invoices.block.paid' },
  { key: 'remaining', labelKey: 'invoices.block.remaining' },
];

/**
 * Полоска Итого / Оплачено / Остаток (ключи invoices.block.*).
 */
export function InvoiceSummaryStrip({ summary, currency }: InvoiceSummaryStripProps): JSX.Element {
  return (
    <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
      {SUMMARY_FIELDS.map((field) => (
        <Box key={field.key}>
          <Typography variant="caption" color="text.secondary">
            {t(field.labelKey)}
          </Typography>
          <Typography variant="body2">{formatMoney(summary[field.key], currency)}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

interface InvoiceDealSummaryProps {
  dealId: string;
  /** Валюта сделки — сводка только по счетам этой валюты. */
  currency?: string | undefined;
}

/**
 * Сводка счетов сделки: общий кэш с InvoiceBlock и полоска Итого/Оплачено/Остаток.
 * Пустой список — нулевая полоска (не прятать молча).
 */
export function InvoiceDealSummary({ dealId, currency }: InvoiceDealSummaryProps): JSX.Element {
  const query = useQuery({
    queryKey: ['invoices', 'deal', dealId],
    queryFn: () => invoicesApi.list({ dealId, page: 0, size: INVOICE_SUMMARY_PAGE_SIZE }),
    enabled: Boolean(dealId),
  });

  if (query.isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('invoices.loading')}
      </Typography>
    );
  }

  const invoices = query.data?.content ?? [];
  const summary = computeSummary(invoices, currency);
  return <InvoiceSummaryStrip summary={summary} currency={currency} />;
}
